/**
 * useMqttAlarms — deriva las alarmas activas a partir del estado MQTT de los
 * reguladores (`ReguladorStatus.alarmas[]`). Reemplaza el antiguo bucle de
 * alarmas aleatorias.
 *
 * Cuando aparece una alarma nueva, mueve la cámara al cruce afectado
 * (auto-encuadre) — salvo mientras corre el tour narrado.
 */
import { useEffect, useRef, useState } from 'react';
import { useColomaMqtt } from '../store/coloma-mqtt';
import { useDemoTour } from '../store/demoTour';
import { useSessionAlarmMutes, isSessionMuted } from '../store/sessionAlarmMutes';
import { useAlarmMasks, isAlarmMasked } from '../store/alarmMasks';
import { santaColomaIntersections } from '../data/santa-coloma';
// SANTA_COLOMA_CENTER/ZOOM/BEARING/PITCH solo las usaba flyToDefault, más
// abajo — descomentar junto con ella si se reactiva.
// import {
//   SANTA_COLOMA_CENTER,
//   SANTA_COLOMA_ZOOM,
//   SANTA_COLOMA_BEARING,
//   SANTA_COLOMA_PITCH,
// } from '../data/santa-coloma';
import { toCanonicalId, toDisplayId } from '../utils/canonicalId';
import { resolveAlarmDescription } from '../data/alarmCatalog';
import type { MockAlarm } from '../components/guadalajara/AlarmPanel';
import type { IntersectionDefinition } from '../types/intersection';

import { CAMERA_URL, CAMERA_HEADERS } from '../utils/camera-api';

// Una alarma resuelta se mantiene visible este tiempo antes de retirarse.
const RESOLVED_LINGER_MS = 30_000;

// El control de "esta alarma no debe mover la cámara ni sonar" es 100%
// manual (enmascarado ARS-09, `isAlarmMasked`) + mute de sesión
// (`isSessionMuted`) — no hay ningún código fijo bloqueado a nivel global.
// Antes existía `CAMERA_MASKED_ALARM_CODES` con 'CD' hardcodeado (pedido de
// ARS93 porque un regulador con driver sin optimizar disparaba demasiado
// seguido) — bloqueaba CD para TODA la flota sin que el operador pudiera
// revertirlo. Ahora, si un regulador concreto molesta, se enmascara desde
// el panel de edición; al desenmascararlo vuelve a sonar/mover cámara de
// inmediato (ver el segundo useEffect más abajo).

// Índice ID canónico → intersección, para localizar el cruce de cada alarma.
const BY_CANONICAL: Map<string, IntersectionDefinition> = new Map(
  santaColomaIntersections.map(ix => [toCanonicalId(ix.cabinet.logicalId), ix]),
);

let nextAlarmId = 1;

/** Vuela la cámara al cruce de una alarma recién aparecida. */
function flyToAlarm(ix: IntersectionDefinition, code: string) {
  const desc = resolveAlarmDescription(code);
  fetch(CAMERA_URL, {
    method: 'POST',
    headers: CAMERA_HEADERS,
    body: JSON.stringify({
      type: 'fly_to',
      center: ix.center,
      zoom: 18.5,
      pitch: 50,
      bearing: 0,
      duration: 2500,
      narrative: `${toDisplayId(ix.cabinet.logicalId)} ${ix.name} — !${desc}!`,
      alarm: { driverId: ix.cabinet.logicalId, code },
    }),
  }).catch(() => {});
}

// Volvía la cámara a la vista por defecto al resolverse la última alarma
// activa — desactivado 2026-09-14 junto con su única llamada, más abajo
// (ver el comentario ahí para el motivo). Se deja comentada, no borrada,
// para reactivarla sin tener que reescribirla.
// function flyToDefault() {
//   fetch(CAMERA_URL, {
//     method: 'POST',
//     headers: CAMERA_HEADERS,
//     body: JSON.stringify({
//       type: 'fly_to',
//       center: SANTA_COLOMA_CENTER,
//       zoom: SANTA_COLOMA_ZOOM,
//       bearing: SANTA_COLOMA_BEARING,
//       pitch: SANTA_COLOMA_PITCH,
//       duration: 2500,
//     }),
//   }).catch(() => {});
// }

export function useMqttAlarms(): MockAlarm[] {
  const reguladores = useColomaMqtt(s => s.reguladores);
  const touring = useDemoTour(s => s.touring);
  const sessionMuted = useSessionAlarmMutes(s => s.muted);
  const fixedMasks = useAlarmMasks(s => s.masks);
  const [alarms, setAlarms] = useState<MockAlarm[]>([]);

  // Registro interno por clave "canonicalId:code" → entrada de alarma.
  const trackedRef = useRef<Map<string, MockAlarm>>(new Map());
  // Último estado de enmascarado evaluado por alarma — permite detectar el
  // flanco "se acaba de desenmascarar, sigue activa" en el segundo efecto.
  const maskedRef = useRef<Map<string, boolean>>(new Map());
  const touringRef = useRef(touring);
  touringRef.current = touring;
  // Refs para no re-disparar el efecto (dependiente de `reguladores`) solo
  // porque cambió un enmascarado — se leen en el momento de evaluar.
  const sessionMutedRef = useRef(sessionMuted);
  sessionMutedRef.current = sessionMuted;
  const fixedMasksRef = useRef(fixedMasks);
  fixedMasksRef.current = fixedMasks;

  function isMasked(canonicalId: string, code: string): boolean {
    return isSessionMuted(sessionMutedRef.current, canonicalId, code)
      || isAlarmMasked(fixedMasksRef.current, canonicalId, code);
  }

  // `muted` va en cada alarma para que contador, badges y vista realista
  // apliquen el mismo criterio que el panel sin recalcular máscaras.
  function publish() {
    setAlarms(
      [...trackedRef.current.values()]
        .map(e => ({ ...e, muted: !e.resolvedAt && isMasked(toCanonicalId(e.driverId), e.code) }))
        .sort((a, b) => b.timestamp - a.timestamp),
    );
  }

  useEffect(() => {
    const tracked = trackedRef.current;
    const now = Date.now();
    const activeKeys = new Set<string>();
    const nuevas: { ix: IntersectionDefinition; code: string; canonicalId: string }[] = [];

    for (const status of Object.values(reguladores)) {
      const ix = BY_CANONICAL.get(status.id);
      if (!ix) continue;
      const codigos = status.online ? (status.alarmas || []) : [];
      for (const code of codigos) {
        const key = `${status.id}:${code}`;
        activeKeys.add(key);
        const existente = tracked.get(key);
        if (!existente) {
          tracked.set(key, { id: nextAlarmId++, driverId: ix.cabinet.logicalId, code, timestamp: now });
          maskedRef.current.set(key, isMasked(status.id, code));
          nuevas.push({ ix, code, canonicalId: status.id });
        } else if (existente.resolvedAt) {
          // reapareció tras resolverse → reactivar y tratar como nueva
          // (dispara de nuevo el toast y el auto-encuadre).
          existente.resolvedAt = undefined;
          existente.timestamp = now;
          maskedRef.current.set(key, isMasked(status.id, code));
          nuevas.push({ ix, code, canonicalId: status.id });
        }
      }
    }

    // Marcar resueltas las que ya no están activas; retirar las viejas.
    for (const [key, e] of tracked) {
      if (!activeKeys.has(key) && !e.resolvedAt) {
        e.resolvedAt = now;
      }
      if (e.resolvedAt && now - e.resolvedAt > RESOLVED_LINGER_MS) {
        tracked.delete(key);
        maskedRef.current.delete(key);
      }
    }

    publish();

    if (touringRef.current) return; // el tour gobierna la cámara

    const nuevasConCamara = nuevas.filter(n => !isMasked(n.canonicalId, n.code));
    if (nuevasConCamara.length > 0) {
      // Auto-encuadre de la última alarma nueva no enmascarada.
      const ultima = nuevasConCamara[nuevasConCamara.length - 1];
      flyToAlarm(ultima.ix, ultima.code);
    }
    // Antes: al resolverse la última alarma activa, la cámara volvía sola a
    // la vista por defecto (`flyToDefault()`, pensado para vigilancia
    // pasiva sin nadie tocando la cámara). Desactivado 2026-09-14 —
    // competía con cualquier acción manual (Modo Edición, consola) pasados
    // los 8s de gracia de cameraControl.ts, y SCG029 (flapping de alarma
    // CD, cliente MQTT sin optimizar) lo disparaba sin parar. Reactivar
    // cuando haya una forma de no pisar una observación manual en curso.
    // if (algunaResuelta) {
    //   const quedanActivas = [...tracked.values()].some(e => !e.resolvedAt);
    //   if (!quedanActivas) flyToDefault();
    // }
  }, [reguladores]);

  // Desenmascarar manualmente una alarma que sigue activa (o levantar un
  // mute de sesión, si algún día se añade esa capacidad) debe mover la
  // cámara y anunciarla por voz de inmediato — no esperar a que cambie el
  // status MQTT, que puede tardar minutos u horas si la alarma es estable.
  useEffect(() => {
    publish();
    if (touringRef.current) return; // el tour gobierna la cámara
    for (const [key, entry] of trackedRef.current) {
      if (entry.resolvedAt) continue; // ya no está activa
      const canonicalId = toCanonicalId(entry.driverId);
      const wasMasked = maskedRef.current.get(key) ?? false;
      const isMaskedNow = isMasked(canonicalId, entry.code);
      if (wasMasked && !isMaskedNow) {
        const ix = BY_CANONICAL.get(canonicalId);
        if (ix) flyToAlarm(ix, entry.code);
      }
      maskedRef.current.set(key, isMaskedNow);
    }
    // Se dispara solo cuando cambia el estado de enmascarado, no en cada
    // heartbeat MQTT — el primer efecto ya cubre ese caso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedMasks, sessionMuted]);

  return alarms;
}
